import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import arrow from "../../../../asset/images/gray-arrow.svg";
import uploadIcon from "../../../../asset/images/upload-file.svg";
import view from "../../../../asset/images/view-eyes.svg";
import download from "../../../../asset/images/download-brokrage.svg";
import rejectedIcon from "../../../../asset/images/crossIcon.svg";
import sendBackIcon from "../../../../asset/images/sentback.svg";
import { Dealer_Mapping } from "../../../../Configuration/API_URL";
import axiosInstance from "../../../../CommonMethhods/axiosInstance";
import {
  decryptData,
  encryptData,
  formatDate,
  toSentenceCase,
} from "../../../../CommonMethhods/CommonMethods";
import CheckerActionModals from "../CheckerActionModals";

const SENT_BACK_STATUSES = ["sendback", "send back", "sent_back"];

// Read-only view of a single (SIN) dealer mapping request, from the checker's
// side - used for Pending / Approved / Rejected / Send Back requests. Pending
// requests additionally get Reject / Send back / Approve actions,
// same endpoints PendingApprovals.jsx uses for its per-row actions.
function CheckerSingleRequestDetails({ batchId, status, rowData, summary, onActionDone }) {
  const request = rowData?.requests?.[0] || {};

  const [pendingAction, setPendingAction] = useState(null); // "approve" | "reject" | "sendback"

  const normalizedStatus = (status || "").toLowerCase();

  const getStatusClass = (value = "") => {
    switch (value.toLowerCase()) {
      case "pending":
        return "status-pending";
      // case "partial":
      //   return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "sendback":
      case "send_back":
      case "send back":
        return "status-sendback";
      default:
        return "";
    }
  };

  const getStatusLabel = (value = "") => {
    const normalized = value.toLowerCase();
    // if (normalized === "partial") return "Partial";
    if (SENT_BACK_STATUSES.includes(normalized)) return "Send Back";
    return toSentenceCase(value);
  };

  const clientNameCode = request?.clientName
    ? `${request.clientName}- ${request?.ucc || ""}`
    : request?.ucc || "-";

  const makerName = rowData?.makerName || summary?.makerName || request?.makerName || "";
  const makerId = rowData?.makerId || summary?.makerId || request?.makerId || "";
  const makerLabel = makerName ? `${makerName}- ${makerId}` : makerId || "-";

  const requestNo = request?.requestId || rowData?.requestId || "-";

  const rejectionRemarks = Array.isArray(request?.rejectionReason)
    ? request.rejectionReason.filter(Boolean).join(", ")
    : request?.rejectionReason || "";

  const sendBackRemarks = Array.isArray(request?.sendBackReason)
    ? request.sendBackReason.filter(Boolean).join(", ")
    : request?.sendBackReason || "";

  const approvalDocLink = rowData?.approvalDocLink || "";

  const formatDealer = (code, name) =>
    code ? (name ? `${code} - ${name}` : code) : "-";

  // Category is the type the maker picked; "Others" carries the maker's own text.
  // Category is the type the maker picked; anything else is shown as "Others"
  // and the maker's explanation is in the remarks.
  const getReasonLabel = (value = "") => {
    const reasonText = (value || "").trim();
    if (!reasonText) return "-";
    if (reasonText.toLowerCase() === "new mapping") return "New Mapping";
    if (reasonText.toLowerCase() === "update existing mapping") return "Update existing mapping";
    // return `Others - ${reasonText}`;
    return "Others";
  };

  const handleViewDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_self", "noopener,noreferrer");
  };

  const isPending = normalizedStatus === "pending";
  const isRejected = normalizedStatus === "rejected";
  const isSendBack = SENT_BACK_STATUSES.includes(normalizedStatus);

  // Old dealer code is optional at submission - when the maker left it blank
  // the backend uses the client's current mapping, so it isn't on the request.
  // While the request is still pending that current mapping is the old dealer.
  const [activeMapping, setActiveMapping] = useState(null);

  useEffect(() => {
    // if (request?.oldDealerCode || !request?.ucc || !isPending) return;
    // Looked up even when a value was saved, so this page shows the same old
    // dealer as the mapping request form (the client's current mapping).
    if (!request?.ucc || !isPending) return;

    let isActive = true;

    axiosInstance
      .get(`${Dealer_Mapping.activeMapping}/${request.ucc}`)
      .then((response) => {
        const decrypted = JSON.parse(decryptData(response?.data));
        if (isActive && decrypted?.status === 0) {
          setActiveMapping(decrypted?.data || null);
        }
      })
      .catch((error) => console.log(error));

    return () => {
      isActive = false;
    };
  }, [request?.ucc, request?.oldDealerCode, isPending]);

  // const oldDealerCode = request?.oldDealerCode || activeMapping?.dealerCode || "";
  // const oldDealerName = request?.oldDealerName || activeMapping?.dealerName || "";
  // const oldBranch = request?.oldBranch || activeMapping?.branch || request?.branch;
  const oldDealerCode = activeMapping?.dealerCode || request?.oldDealerCode || "";
  const oldDealerName = activeMapping?.dealerName || request?.oldDealerName || "";
  const oldBranch = activeMapping?.branch || request?.oldBranch || request?.branch;

  // Resolves true when the action went through, so the modal can show its
  // success step.
  const handleAction = async (action, reason = "") => {
    const endpoint = `${Dealer_Mapping.singleMappingSubmit}/${batchId}/row-status`;
    const payload = {
      requestId: request?.requestId,
      action,
      ...(reason && { reason }),
    };

    try {
      const encryptedPayload = encryptData(JSON.stringify(payload));
      const response = await axiosInstance.put(endpoint, encryptedPayload, {
        transformRequest: [(data) => data],
      });
      const decrypted = JSON.parse(decryptData(response.data));
      if (decrypted?.status === 0) return true;
      toast.error(decrypted?.message);
      return false;
    } catch (error) {
      toast.error("Something went wrong");
      return false;
    }
  };

  return (
    <>
      <div className="ckr-section">
        <p className="ckr-section-title mb-15">Checker Review Queue</p>

        <div className="dlr-request-info">
          <div className="dlr-info-item">
            <span className="dlr-info-label">Client Name &amp; Code</span>
            <span className="dlr-info-value">{clientNameCode}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Request No.</span>
            <span className="dlr-info-value">{requestNo}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Maker</span>
            <span className="dlr-info-value">{makerLabel}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Effective Date</span>
            <span className="dlr-info-value">
              {request?.startDate ? formatDate(request.startDate) : "-"}
            </span>
          </div>
        </div>

        {/* <div className="status-content"> */}
        {/* The status badge sat directly on the comparison box below it. */}
        <div className="status-content mb-15">
          <span className="dlr-sub-title">Dealer Mapping Comparison</span>
          {status ? (
            <span className={getStatusClass(status)}>{getStatusLabel(status)}</span>
          ) : null}
        </div>

        <div className="rm-comparison mb-20">
          <div className="rm-comparison-card old">
            <div className="comparison-title">OLD</div>

            <div className="comparison-row">
              <span>Dealer</span>
              {/* <strong>{formatDealer(oldDealerCode, oldDealerName)}</strong> */}
              <strong>
                {oldDealerCode ? formatDealer(oldDealerCode, oldDealerName) : "NA"}
              </strong>
            </div>

            <div className="comparison-row">
              <span>Branch</span>
              <strong>{oldBranch || "-"}</strong>
            </div>
          </div>

          <img src={arrow} alt="arrow" />

          <div className="rm-comparison-card new">
            <div className="comparison-title">NEW</div>

            <div className="comparison-row">
              <span>New</span>
              <strong>{formatDealer(request?.newDealerCode, request?.newDealerName)}</strong>
            </div>

            <div className="comparison-row">
              <span>Branch</span>
              <strong>{request?.branch || "-"}</strong>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <span className="dlr-sub-title">Reason Type</span>
            <input
              type="text"
              className="rm-reason-input mb-20"
              value={getReasonLabel(request?.reason || summary?.reason)}
              readOnly
              disabled
            />
          </div>

          <div className="col-md-6">
            <span className="dlr-sub-title">Reason</span>
            <input
              type="text"
              className="rm-reason-input mb-20"
              value={request?.remarks || summary?.remarks || "-"}
              readOnly
              disabled
            />
          </div>
        </div>

        {rowData?.approvalDocFilename && (
          <>
            <span className="dlr-sub-title">Attachment</span>

            <div className="rm-attachment dlr-attachment">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="file-tooltips">{rowData?.approvalDocFilename}</Tooltip>}
              >
                <div className="attachment-name">
                  <img src={uploadIcon} alt="upload icon" />
                  <span className="file-name">{rowData?.approvalDocFilename}</span>
                </div>
              </OverlayTrigger>

              <div className="attachment-actions">
                <button type="button" onClick={handleViewDoc}>
                  <img src={view} alt="view" className="view-icon" />
                  <span className="btn-title">View</span>
                </button>

                <button type="button" onClick={handleDownloadDoc}>
                  <img src={download} alt="download" />
                  <span className="btn-title">Download</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {isRejected && rejectionRemarks && (
        <div className="reject-remark-cont">
          <div>
            <img src={rejectedIcon} alt="rejected" />
          </div>
          <div className="wrap-content">
            <span className="bold-text">Checker Rejection Remarks</span>
            <span className="remarks-val">{rejectionRemarks}</span>
          </div>
        </div>
      )}

      {isSendBack && (
        <>
          {sendBackRemarks && (
            <div className="reject-remark-cont">
              <div>
                <img src={rejectedIcon} alt="remarks" />
              </div>
              <div className="wrap-content">
                <span className="bold-text">Checker Rejection Remarks</span>
                <span className="remarks-val">{sendBackRemarks}</span>
              </div>
            </div>
          )}

          <div className="reject-remark-cont">
            <div>
              <img src={sendBackIcon} alt="return" />
            </div>
            <div className="wrap-content">
              <span className="bold-text">Return to Maker for Review</span>
              <span className="remarks-val">{sendBackRemarks || "-"}</span>
            </div>
          </div>
        </>
      )}

      {isPending && (
        <div className="mapping-footer-actions">
          <button type="button" className="reject-btn filled" onClick={() => setPendingAction("reject")}>
            Reject
          </button>

          <button type="button" className="back-btn" onClick={() => setPendingAction("sendback")}>
            Send back
          </button>

          <button type="button" className="submit-btns" onClick={() => setPendingAction("approve")}>
            Approve
          </button>
        </div>
      )}

      {pendingAction && (
        <CheckerActionModals
          action={pendingAction}
          onCancel={() => setPendingAction(null)}
          onSubmit={handleAction}
          onDone={() => {
            setPendingAction(null);
            onActionDone?.();
          }}
        />
      )}
    </>
  );
}

export default CheckerSingleRequestDetails;
